import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Languages, Loader2, Check, X, Copy } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Translation {
  key: string;
  fr: string;
  en?: string;
  'lg-fr'?: string;
  sw?: string;
}

export const AutoTranslator = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationKey, setTranslationKey] = useState('');
  const [frenchText, setFrenchText] = useState('');
  const [translations, setTranslations] = useState<Translation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTranslate = async () => {
    if (!translationKey || !frenchText) {
      toast({
        title: t('admin.cms.error'),
        description: 'Veuillez remplir tous les champs',
        variant: "destructive"
      });
      return;
    }

    setIsTranslating(true);
    setError(null);
    setTranslations(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('auto-translate-keys', {
        body: {
          key: translationKey,
          frenchText: frenchText,
          targetLanguages: ['en', 'lg-fr', 'sw']
        }
      });

      if (functionError) {
        throw functionError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setTranslations({
        key: translationKey,
        ...data.translations
      });

      toast({
        title: "Traduction réussie",
        description: "Les traductions ont été générées avec succès"
      });
    } catch (error: any) {
      console.error('Translation error:', error);
      const errorMessage = error.message || 'Erreur lors de la traduction';
      setError(errorMessage);
      toast({
        title: t('admin.cms.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copié",
      description: "Le texte a été copié dans le presse-papiers"
    });
  };

  const copyAllTranslations = () => {
    if (!translations) return;
    
    const code = `// Ajoutez ces traductions à useLanguage.tsx

// Français (fr)
'${translations.key}': '${translations.fr}',

// English (en)
'${translations.key}': '${translations.en || ''}',

// Lingala (lg-fr)
'${translations.key}': '${translations['lg-fr'] || ''}',

// Swahili (sw)
'${translations.key}': '${translations.sw || ''}',`;

    copyToClipboard(code);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Languages className="h-5 w-5" />
          Traduction automatique
        </CardTitle>
        <CardDescription>
          Utilisez l'IA pour traduire automatiquement les nouvelles clés dans toutes les langues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Form */}
        <div className="space-y-4">
          <div>
            <Label>Clé de traduction</Label>
            <Input
              value={translationKey}
              onChange={(e) => setTranslationKey(e.target.value)}
              placeholder="ex: admin.new_feature.title"
            />
          </div>
          <div>
            <Label>Texte en français</Label>
            <Textarea
              value={frenchText}
              onChange={(e) => setFrenchText(e.target.value)}
              placeholder="Entrez le texte en français à traduire"
              rows={3}
            />
          </div>
          <Button 
            onClick={handleTranslate} 
            disabled={isTranslating || !translationKey || !frenchText}
            className="w-full"
          >
            {isTranslating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Traduction en cours...
              </>
            ) : (
              <>
                <Languages className="h-4 w-4 mr-2" />
                Traduire automatiquement
              </>
            )}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <X className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Translations Results */}
        {translations && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                Traductions générées
              </h3>
              <Button variant="outline" size="sm" onClick={copyAllTranslations}>
                <Copy className="h-4 w-4 mr-2" />
                Copier tout
              </Button>
            </div>

            <div className="grid gap-4">
              {/* French */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span className="text-xl">🇫🇷</span>
                    Français
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(translations.fr)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <code className="text-sm">{translations.fr}</code>
                </div>
              </div>

              {/* English */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span className="text-xl">🇬🇧</span>
                    English
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(translations.en || '')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <code className="text-sm">{translations.en}</code>
                </div>
              </div>

              {/* Lingala */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span className="text-xl">🇨🇩</span>
                    Lingala
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(translations['lg-fr'] || '')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <code className="text-sm">{translations['lg-fr']}</code>
                </div>
              </div>

              {/* Swahili */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <span className="text-xl">🇹🇿</span>
                    Swahili
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(translations.sw || '')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <code className="text-sm">{translations.sw}</code>
                </div>
              </div>
            </div>

            <Alert>
              <AlertDescription>
                💡 Vérifiez et validez les traductions avant de les ajouter à votre code. 
                Utilisez le bouton "Copier tout" pour copier le code formaté prêt à être ajouté dans useLanguage.tsx.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
